import type { Client, UIClient } from "../types/client.types";
import { formatSubscriptionDate } from "@/lib/dateUtils";

/**
 * Transforms API client data to UI client data
 */
export const transformClientForUI = (client: Client): UIClient => {
  return {
    id: client.user_id,
    name: client.name,
    email: client.email,
    plan: client.subscription_plan,
    subscriptionEndDate: formatSubscriptionDate(client.subscription_until),
    avatar: undefined, // API doesn't provide avatar, will use default
    tokensCount: client.tokens_count,
    quotaRemaining: client.quota_remaining,
    dateCreated: client.date_created,
  };
};

/**
 * Transforms array of API clients to UI clients
 */
export const transformClientsForUI = (clients: Client[]): UIClient[] => {
  return clients.map(transformClientForUI);
};

/**
 * Formats usage percentage to display format
 */
export const formatUsagePercentage = (percentage: number): string => {
  return `${percentage.toFixed(1)}%`;
};

/**
 * Formats large numbers with commas
 */
export const formatNumber = (num: number): string => {
  return num.toLocaleString();
};

/**
 * Calculates usage percentage from current usage and total quota
 */
export const calculateUsagePercentage = (
  used: number,
  total: number,
): number => {
  if (total === 0) return 0;
  return (used / total) * 100;
};

/**
 * Gets status color based on activity status
 */
export const getActivityStatusColor = (
  status: "Success" | "Failed" | "Pending",
): string => {
  const statusColors = {
    Success: "#10b981",
    Failed: "#FF4444",
    Pending: "#FFA500",
  };
  return statusColors[status] || "#808080";
};

/**
 * Generates a temporary password
 */

export const generatePassword = () => {
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
  const numbers = "0123456789";
  const specials = "!@#$%^*";
  const alphanum = letters + numbers;

  let result = "";

  result += specials.charAt(Math.floor(Math.random() * specials.length));

  const poolWithLimitedSpecials = alphanum + specials;
  result += poolWithLimitedSpecials.charAt(
    Math.floor(Math.random() * poolWithLimitedSpecials.length),
  );

  for (let i = 0; i < 6; i++) {
    result += alphanum.charAt(Math.floor(Math.random() * alphanum.length));
  }

  return result
    .split("")
    .sort(() => 0.5 - Math.random())
    .join("");
};
